import { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

/**
 * Custom hook for mobile detection and responsive utilities
 * Provides device type detection and responsive helpers
 */
// eslint-disable-next-line max-lines-per-function
export const useMobileDetection = () => {
  const theme = useTheme();

  // Material-UI breakpoint queries
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600px - 900px
  const isDesktop = useMediaQuery(theme.breakpoints.up('md')); // >= 900px

  // More specific breakpoints
  useMediaQuery(theme.breakpoints.down('xs')); // < 0px (effectively never, xs starts at 0);
  const isSmallMobile = useMediaQuery('(max-width:375px)'); // iPhone SE and smaller
  const isLargeMobile = useMediaQuery('(min-width:376px) and (max-width:599px)'); // Larger phones

  // Touch device detection
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // PWA detection
  const [isPWA, setIsPWA] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  // Orientation
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const isLandscape = useMediaQuery('(orientation: landscape)');

  // Viewport dimensions
  const [viewport, setViewport] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  useEffect(() => {
    // Check if touch device
    const checkTouch = () => {
      setIsTouchDevice(
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        navigator.msMaxTouchPoints > 0
      );
    };

    // Check if PWA
    const checkPWA = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                          window.navigator.standalone === true;
      setIsPWA(isStandalone);
    };

    // Check if iOS
    const checkIOS = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      setIsIOS(/iphone|ipad|ipod/.test(userAgent));
    };

    // Update viewport dimensions
    const handleResize = () => {
      setViewport({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    checkTouch();
    checkPWA();
    checkIOS();
    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Helper functions
  const getResponsiveValue = (mobile, tablet, desktop) => {
    if (isMobile) return mobile;
    if (isTablet) return tablet;
    return desktop;
  };

  const getTouchTargetSize = () => {
    // WCAG guidelines: minimum 44x44px for iOS, 48x48px for Android
    return isIOS ? 44 : 48;
  };

  const getDrawerWidth = () => {
    if (isMobile) return viewport.width * 0.85; // 85% of screen on mobile
    if (isTablet) return 400; // Fixed width on tablet
    return 500; // Larger fixed width on desktop
  };

  const getChatBotPosition = () => {
    if (isMobile) {
      return {
        bottom: 16,
        right: 16,
        position: 'fixed',
        zIndex: 1200,
      };
    }
    return {
      bottom: 24,
      right: 24,
      position: 'fixed',
      zIndex: 1200,
    };
  };

  const shouldAutoCollapse = () => {
    // Auto-collapse on mobile to save space
    return isMobile || (isTablet && isPortrait);
  };

  const getToolbarLayout = () => {
    // Determine if toolbar should be vertical or horizontal
    if (isMobile && isPortrait) return 'vertical';
    return 'horizontal';
  };

  return {
    // Device detection
    isMobile,
    isTablet,
    isDesktop,
    isSmallMobile,
    isLargeMobile,
    isTouchDevice,
    isPWA,
    isIOS,

    // Orientation
    isPortrait,
    isLandscape,

    // Viewport
    viewport,

    // Helper functions
    getResponsiveValue,
    getTouchTargetSize,
    getDrawerWidth,
    getChatBotPosition,
    shouldAutoCollapse,
    getToolbarLayout,

    // Convenience values
    minTouchSize: getTouchTargetSize(),
    drawerWidth: getDrawerWidth(),
    chatPosition: getChatBotPosition(),
  };
};

export default useMobileDetection;